import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import DNALoader from "../components/DNALoader";
import { getIngredient } from "../lib/api";
import SEO, { canonicalFor, titleFor } from "../lib/seo";

const ratingColor = (r) => (r === "red" ? "#FF3B3B" : r === "yellow" ? "#FFB800" : "#00C853");
const ratingLabel = (r) => (r === "red" ? "HIGH RISK" : r === "yellow" ? "MODERATE" : "SAFE");

const Block = ({ label, children }) =>
  children ? (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-2">{label}</div>
      <p className="text-[color:var(--text-2)] leading-relaxed">{children}</p>
    </div>
  ) : null;

export default function IngredientDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    getIngredient(slug)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Ingredient not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-24"><DNALoader label="Loading ingredient..." /></div>;

  if (err)
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <AlertTriangle className="h-10 w-10 text-[color:var(--danger)] mx-auto mb-4" />
        <h2 className="font-display text-2xl font-semibold mb-2">Not found</h2>
        <p className="text-[color:var(--text-2)] mb-6">{err}</p>
        <Link to="/ingredients" className="lime-btn rounded-lg px-4 py-2 text-sm">All ingredients</Link>
      </div>
    );

  if (!data) return null;

  const c = data.curated;
  const color = ratingColor(data.overallRating);

  return (
    <div data-testid="ingredient-detail-page" className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <SEO
        title={titleFor(`${data.name}${data.eNumber ? ` (${data.eNumber})` : ""}: Is It Safe?`)}
        description={c?.summary || `${data.name} — safety rating, health concerns and the products that contain it. Traffic-light ingredient analysis by WhatIsInside.`}
        canonical={canonicalFor(`/ingredient/${slug}`)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${data.name}: Safety, Uses and Health Concerns`,
          description: c?.summary || `Safety analysis of ${data.name}.`,
          about: data.name,
        }}
      />

      <Link to="/ingredients" data-testid="ingredient-back" className="ghost-btn rounded-lg px-3 py-1.5 text-sm inline-flex items-center gap-2 mb-8">
        <ArrowLeft className="h-4 w-4" /> All ingredients
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
        <div>
          {data.eNumber && (
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-2">{data.eNumber}</div>
          )}
          <h1 data-testid="ingredient-name" className="font-display text-4xl md:text-6xl font-bold tracking-tighter">{data.name}</h1>
          {c?.aka && <div className="text-sm text-[color:var(--text-3)] mt-2">Also known as {c.aka}</div>}
        </div>
        <span
          className="font-mono text-xs tracking-[0.15em] uppercase px-3 py-1.5 rounded"
          style={{ color, background: `${color}18`, border: `1px solid ${color}40` }}
          data-testid="ingredient-rating"
        >
          {ratingLabel(data.overallRating)}
        </span>
      </div>

      {c?.summary && <p className="text-lg text-[color:var(--text-2)] leading-relaxed mb-8 max-w-2xl">{c.summary}</p>}

      {c && (
        <div className="surface rounded-xl p-6 space-y-6 mb-10">
          <Block label="What it is">{c.whatItIs}</Block>
          <Block label="Health concern">{c.healthConcern}</Block>
          <Block label="The verdict">{c.verdict}</Block>
        </div>
      )}

      <div className="mb-4">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-1">Found in</div>
        <h2 className="font-display text-2xl md:text-3xl font-semibold">
          {data.productCount} analyzed product{data.productCount === 1 ? "" : "s"}
        </h2>
      </div>

      {data.products.length === 0 ? (
        <div className="surface rounded-xl p-8 text-center text-[color:var(--text-2)]">
          No analyzed products contain this yet. Search a product on the home page and it'll appear here.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ingredient-products">
          {data.products.map((p, i) => {
            const pc = ratingColor(p.ingredientRating);
            return (
              <Link
                key={p.slug || i}
                to={`/product/${p.slug}`}
                data-testid={`ingredient-product-${i}`}
                className="surface rounded-xl p-4 flex items-start justify-between gap-4 hover:border-[color:var(--lime)]/40 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{p.productName}</div>
                  <div className="text-xs text-[color:var(--text-3)] truncate">{p.brand || "—"}</div>
                  {p.whyRated && <p className="text-sm text-[color:var(--text-2)] mt-2 leading-relaxed">{p.whyRated}</p>}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <span className="font-mono text-2xl font-bold" style={{ color: ratingColor(p.overallRating) }}>
                    {p.overallScore ?? "—"}
                  </span>
                  <span className="font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded" style={{ color: pc, border: `1px solid ${pc}40` }}>
                    {ratingLabel(p.ingredientRating)}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
